import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { createRedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { StorefrontPreviewTokenContext } from "@redotech/redo-web/selector-picker/preview-token";
import { Context, createContext, ReactNode, useMemo } from "react";
import { clientVersionHeaders } from "./client-version-headers";

export const RedoMerchantRpcClientContext: Context<
  RedoMerchantRpcClient | undefined
> = createContext<RedoMerchantRpcClient | undefined>(undefined);
RedoMerchantRpcClientContext.displayName = "RedoMerchantRpcClientContext";

export function RedoMerchantRpcClientProvider({
  children,
  baseUrl,
  authToken,
}: {
  children: ReactNode | ReactNode[];
  baseUrl: string;
  authToken?: string;
}) {
  const redoMerchantRpcClient = useMemo(() => {
    if (!authToken) {
      return undefined;
    }
    return createRedoMerchantRpcClient({
      baseURL: new URL(`${baseUrl}/rpc`),
      headers: { ...clientVersionHeaders, Authorization: authToken },
    });
  }, [baseUrl, authToken]);
  return (
    <RedoMerchantRpcClientContext.Provider value={redoMerchantRpcClient}>
      {redoMerchantRpcClient ? (
        <MerchantStorefrontPreviewTokenProvider>
          {children}
        </MerchantStorefrontPreviewTokenProvider>
      ) : (
        children
      )}
    </RedoMerchantRpcClientContext.Provider>
  );
}

function MerchantStorefrontPreviewTokenProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const getPreviewToken = useHandler(
    async () => (await rpcClient.getStorefrontPreviewToken({})).token,
  );
  return (
    <StorefrontPreviewTokenContext.Provider value={getPreviewToken}>
      {children}
    </StorefrontPreviewTokenContext.Provider>
  );
}
